import React, { Fragment } from 'react';
import { toast } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { addIOCData, getIOCLists, uploadIOCData } from '../../redux/actions/configActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import Content from '../../components/Content';
import Table, { TableButton } from '../../components/Table';
import TextInput from '../../components/TextInput';
import AddIOCModal from './AddIOCModal';
import Pagination from '../../components/Pagination';
import Loader from '../../components/Loader';
import NetworkError from '../../components/Error';

import IOCListStyles from './IOCLists.module.css';
import DeleteIOCModal from './DeleteIOCModal';

const FRONTEND_IOC_LISTS_ERROR_CODES = {
	INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	ADD_ENTRY_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	UPLOAD_ENTRY_FAILED: ErrorCodes.FILE_PROCESSING_FAILED,
	DELETE_ENTRY_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	REFRESH_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildIOCListsSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({
		errorCode,
		pageInfo,
		sourceMessage,
	});

const buildEmptyIOCData = () => ({
	FQDNCols: [ 'FQDN' ],
	FQDNRows: [],
	IPSCols: [ 'IP Address' ],
	IPSRows: [],
	HashCols: [ 'Hash' ],
	HashRows: [],
	status: true
});

const IOCListsScreen = () => {
	const dispatch = useDispatch();
	const userLogin = useSelector((state) => state.userLogin);

	const [ userRole, setUserRole ] = React.useState('');
	const [ addIOCModalStatus, setAddIOCModalStatus ] = React.useState({ status: false, type: '' });
	const [ deleteIOCModalStatus, setDeleteIOCModalStatus ] = React.useState({ status: false, type: '', data: '' });

	const [ dataLoading, setDataLoading ] = React.useState(false);
	const [ newIOCText, setNewIOCText ] = React.useState({ value: '', error: '' });
	const [ newIOCFile, setNewIOCFile ] = React.useState({ value: null, error: '' });

	const [ iocData, setIocData ] = React.useState({});
	const [ searchFQDNKey, setSearchFQDNKey ] = React.useState('');
	const [ searchIPKey, setSearchIPKey ] = React.useState('');
	const [ searchHashKey, setSearchHashKey ] = React.useState('');
	const [ supportErrorMessage, setSupportErrorMessage ] = React.useState('');
	const [ copiedSupportError, setCopiedSupportError ] = React.useState(false);

	const [ FQDNTotalRows, setFQDNTotalRows ] = React.useState(0);
	const [ FQDNRows, setFQDNRows ] = React.useState([]);

	const [ IPTotalRows, setIPTotalRows ] = React.useState(0);
	const [ IPRows, setIPRows ] = React.useState([]);

	const [ HashTotalRows, setHashTotalRows ] = React.useState(0);
	const [ HashRows, setHashRows ] = React.useState([]);

	const [ pageNumberFQDN, setPageNumberFQDN ] = React.useState(1);
	const [ pageNumberIP, setPageNumberIP ] = React.useState(1);
	const [ pageNumberHash, setPageNumberHash ] = React.useState(1);
	const pageLimit = 10;

	const setIOCListsSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setSupportErrorMessage(buildIOCListsSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearIOCListsSupportError = React.useCallback(() => {
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [ supportErrorMessage ]);

	React.useEffect(
		() => {
			if (
				userLogin &&
				userLogin.userInfo &&
				userLogin.userInfo.status === true &&
				userLogin.userInfo.session &&
				userLogin.userInfo.session.role
			) {
				setUserRole(userLogin.userInfo.session.role);
			}
		},
		[ userLogin ]
	);

	React.useEffect(
		() => {
			const getData = async () => {
				setDataLoading(true);
				clearIOCListsSupportError();
				try {
					const data = await dispatch(getIOCLists());
					if (data && data.status && data.FQDNRows && data.IPSRows) {
						setIocData(data);
						clearIOCListsSupportError();
					} else {
						setIocData(buildEmptyIOCData());
						setIOCListsSupportError(
							FRONTEND_IOC_LISTS_ERROR_CODES.INITIAL_LOAD_FAILED,
							'IOC Lists Page / Initial Data Load Failed',
							data?.msg || (typeof data === 'string' ? data : 'IOC lists request did not return success')
						);
					}
				} catch (error) {
					setIocData(buildEmptyIOCData());
					setIOCListsSupportError(
						FRONTEND_IOC_LISTS_ERROR_CODES.INITIAL_LOAD_FAILED,
						'IOC Lists Page / Initial Data Request Error',
						error?.message || String(error)
					);
				} finally {
					setDataLoading(false);
				}
			};

			getData();
		},
		[ dispatch, clearIOCListsSupportError, setIOCListsSupportError ]
	);

	React.useEffect(
		() => {
			if (iocData && Object.keys(iocData).length > 0 && iocData.FQDNRows && iocData.FQDNRows.length > 0) {
				if (searchFQDNKey) {
					const FQDNData = iocData.FQDNRows.filter((row) => row.find((el) => String(el ?? '').toLowerCase().includes(String(searchFQDNKey ?? '').toLowerCase())));
					setFQDNTotalRows(FQDNData.length);
					setFQDNRows(FQDNData.slice(0, pageLimit));
				} else {
					setFQDNTotalRows(iocData.FQDNRows.length);
					setFQDNRows(iocData.FQDNRows.slice(0, pageLimit));
				}
			} else {
				setFQDNTotalRows(0);
				setFQDNRows([]);
			}
		},
		[ iocData, searchFQDNKey ]
	);

	React.useEffect(
		() => {
			if (iocData && Object.keys(iocData).length > 0 && iocData.IPSRows && iocData.IPSRows.length > 0) {
				if (searchIPKey) {
					const IPRowData = iocData.IPSRows.filter((row) => row.find((el) => String(el ?? '').toLowerCase().includes(String(searchIPKey ?? '').toLowerCase())));
					setIPTotalRows(IPRowData.length);
					setIPRows(IPRowData.slice(0, pageLimit));
				} else {
					setIPTotalRows(iocData.IPSRows.length);
					setIPRows(iocData.IPSRows.slice(0, pageLimit));
				}
			} else {
				setIPTotalRows(0);
				setIPRows([]);
			}
		},
		[ iocData, searchIPKey ]
	);

	React.useEffect(
		() => {
			if (iocData && Object.keys(iocData).length > 0 && iocData.HashRows && iocData.HashRows.length > 0) {
				if (searchHashKey) {
					const HashRowData = iocData.HashRows.filter((row) => row.find((el) => String(el ?? '').toLowerCase().includes(String(searchHashKey ?? '').toLowerCase())));
					setHashTotalRows(HashRowData.length);
					setHashRows(HashRowData.slice(0, pageLimit));
				} else {
					setHashTotalRows(iocData.HashRows.length);
					setHashRows(iocData.HashRows.slice(0, pageLimit));
				}
			} else {
				setHashTotalRows(0);
				setHashRows([]);
			}
		},
		[ iocData, searchHashKey ]
	);

	let nextFQDNContentFrom = 0;
	const handleFQDNPageChange = async (currentPage) => {
		setPageNumberFQDN(currentPage.selected + 1);

		nextFQDNContentFrom = currentPage.selected * pageLimit;
		const nextContentTo = currentPage.selected * pageLimit + pageLimit;
		setFQDNRows(
			iocData &&
				Object.keys(iocData).length > 0 &&
				iocData.FQDNRows.length > 0 &&
				iocData.FQDNRows
					.filter((row) => row.find((el) => String(el ?? '').toLowerCase().includes(String(searchFQDNKey ?? '').toLowerCase())))
					.slice(nextFQDNContentFrom, nextContentTo)
		);
	};

	let nextIPContentFrom = 0;
	const handleIPPageChange = async (currentPage) => {
		setPageNumberIP(currentPage.selected + 1);

		nextIPContentFrom = currentPage.selected * pageLimit;
		const nextContentTo = currentPage.selected * pageLimit + pageLimit;
		setIPRows(
			iocData &&
				Object.keys(iocData).length > 0 &&
				iocData.IPSRows.length > 0 &&
				iocData.IPSRows
					.filter((row) => row.find((el) => String(el ?? '').toLowerCase().includes(String(searchIPKey ?? '').toLowerCase())))
					.slice(nextIPContentFrom, nextContentTo)
		);
	};

	let nextHashContentFrom = 0;
	const handleHashPageChange = async (currentPage) => {
		setPageNumberHash(currentPage.selected + 1);

		nextHashContentFrom = currentPage.selected * pageLimit;
		const nextContentTo = currentPage.selected * pageLimit + pageLimit;
		setHashRows(
			iocData &&
				Object.keys(iocData).length > 0 &&
				iocData.HashRows.length > 0 &&
				iocData.HashRows
					.filter((row) => row.find((el) => String(el ?? '').toLowerCase().includes(String(searchHashKey ?? '').toLowerCase())))
					.slice(nextHashContentFrom, nextContentTo)
		);
	};

	const closeModal = () => {
		setAddIOCModalStatus({ status: false, type: '' });
		setNewIOCText({ value: '', error: '' });
		setNewIOCFile({ value: null, error: '' });
	};

	const handleAddIOC = async () => {
		if (!iocData?.isProvider) return;

		if (newIOCText.value !== '') {
			if (newIOCText.value.trim() === '') {
				setNewIOCText((previous) => ({ ...previous, error: 'Please enter input value' }));
				return;
			}

			try {
				const formData = new FormData();
				if (addIOCModalStatus.type === 'FQDN') {
					formData.append('btn', 'AddFQDN');
					formData.append('AddFQDN', newIOCText.value);
				} else if (addIOCModalStatus.type === 'IP') {
					formData.append('btn', 'AddIP');
					formData.append('AddIP', newIOCText.value);
				} else if (addIOCModalStatus.type === 'Hash') {
					formData.append('btn', 'AddHash');
					formData.append('AddHash', newIOCText.value);
				}

				const data = await dispatch(addIOCData(formData));
				if (data && data.status) {
					setIocData(data);
					closeModal();
					clearIOCListsSupportError();
					toast.success(`Successfully added ${addIOCModalStatus.type}`);
				} else {
					const sourceMessage =
						data?.msg || (typeof data === 'string' ? data : 'IOC add request did not return success');
					const formattedError = buildIOCListsSupportError(
						FRONTEND_IOC_LISTS_ERROR_CODES.ADD_ENTRY_FAILED,
						'IOC Lists Page / Add Entry Failed',
						sourceMessage
					);
					setNewIOCText((previous) => ({ ...previous, error: formattedError }));
					toast.error(formattedError);
					setIOCListsSupportError(
						FRONTEND_IOC_LISTS_ERROR_CODES.ADD_ENTRY_FAILED,
						'IOC Lists Page / Add Entry Failed',
						sourceMessage
					);
				}
			} catch (error) {
				const sourceMessage = error?.message || String(error);
				const formattedError = buildIOCListsSupportError(
					FRONTEND_IOC_LISTS_ERROR_CODES.ADD_ENTRY_FAILED,
					'IOC Lists Page / Add Entry Request Error',
					sourceMessage
				);
				setNewIOCText((previous) => ({ ...previous, error: formattedError }));
				toast.error(formattedError);
				setIOCListsSupportError(
					FRONTEND_IOC_LISTS_ERROR_CODES.ADD_ENTRY_FAILED,
					'IOC Lists Page / Add Entry Request Error',
					sourceMessage
				);
			}
			return;
		}

		if (!newIOCFile.value) {
			setNewIOCFile((previous) => ({ ...previous, error: 'Please select input file' }));
			return;
		}

		let uploadData;
		try {
			const formData = new FormData();
			if (addIOCModalStatus.type === 'FQDN') {
				formData.append('FQDNfile', newIOCFile.value);
				uploadData = await dispatch(uploadIOCData('upload-fqdn', formData));
			} else if (addIOCModalStatus.type === 'IP') {
				formData.append('IPfile', newIOCFile.value);
				uploadData = await dispatch(uploadIOCData('upload-ipioc', formData));
			}
		} catch (error) {
			const sourceMessage = error?.message || String(error);
			const formattedError = buildIOCListsSupportError(
				FRONTEND_IOC_LISTS_ERROR_CODES.UPLOAD_ENTRY_FAILED,
				'IOC Lists Page / Upload Entry Request Error',
				sourceMessage
			);
			setNewIOCFile((previous) => ({ ...previous, error: formattedError }));
			toast.error(formattedError);
			setIOCListsSupportError(
				FRONTEND_IOC_LISTS_ERROR_CODES.UPLOAD_ENTRY_FAILED,
				'IOC Lists Page / Upload Entry Request Error',
				sourceMessage
			);
			return;
		}

		if (!uploadData || !uploadData.status) {
			const sourceMessage =
				uploadData?.msg || (typeof uploadData === 'string' ? uploadData : 'IOC upload request did not return success');
			const formattedError = buildIOCListsSupportError(
				FRONTEND_IOC_LISTS_ERROR_CODES.UPLOAD_ENTRY_FAILED,
				'IOC Lists Page / Upload Entry Failed',
				sourceMessage
			);
			setNewIOCFile((previous) => ({ ...previous, error: formattedError }));
			toast.error(formattedError);
			setIOCListsSupportError(
				FRONTEND_IOC_LISTS_ERROR_CODES.UPLOAD_ENTRY_FAILED,
				'IOC Lists Page / Upload Entry Failed',
				sourceMessage
			);
			return;
		}

		closeModal();
		setDataLoading(true);
		try {
			const refreshedData = await dispatch(getIOCLists());
			if (refreshedData && refreshedData.status && refreshedData.FQDNRows && refreshedData.IPSRows) {
				setIocData(refreshedData);
				clearIOCListsSupportError();
				toast.success(uploadData.msg || `Successfully added ${addIOCModalStatus.type}`);
			} else {
				setIocData(buildEmptyIOCData());
				const sourceMessage =
					refreshedData?.msg ||
					(typeof refreshedData === 'string' ? refreshedData : 'IOC refresh after upload did not return success');
				const formattedError = buildIOCListsSupportError(
					FRONTEND_IOC_LISTS_ERROR_CODES.REFRESH_FAILED,
					'IOC Lists Page / Refresh After Upload Failed',
					sourceMessage
				);
				toast.error(formattedError);
				setIOCListsSupportError(
					FRONTEND_IOC_LISTS_ERROR_CODES.REFRESH_FAILED,
					'IOC Lists Page / Refresh After Upload Failed',
					sourceMessage
				);
			}
		} catch (error) {
			const sourceMessage = error?.message || String(error);
			const formattedError = buildIOCListsSupportError(
				FRONTEND_IOC_LISTS_ERROR_CODES.REFRESH_FAILED,
				'IOC Lists Page / Refresh After Upload Request Error',
				sourceMessage
			);
			toast.error(formattedError);
			setIOCListsSupportError(
				FRONTEND_IOC_LISTS_ERROR_CODES.REFRESH_FAILED,
				'IOC Lists Page / Refresh After Upload Request Error',
				sourceMessage
			);
		} finally {
			setDataLoading(false);
		}
	};

	const closeDeleteModal = () => {
		setDeleteIOCModalStatus({ status: false, type: '', data: '' });
	};

	const handleDeleteIOC = async () => {
		if (!iocData?.isProvider) return;

		const formData = new FormData();
		if (deleteIOCModalStatus.type === 'FQDN') {
			formData.append('btn', 'DeleteFQDN');
			formData.append('DelFQDN', deleteIOCModalStatus.data);
		} else if (deleteIOCModalStatus.type === 'IP') {
			formData.append('btn', 'DeleteIP');
			formData.append('DelIP', deleteIOCModalStatus.data);
		}

		try {
			const data = await dispatch(addIOCData(formData));
			if (data && data.status) {
				setIocData(data);
				closeDeleteModal();
				clearIOCListsSupportError();
				toast.success(`Successfully deleted ${deleteIOCModalStatus.type}`);
				deleteIOCModalStatus.type === 'FQDN' ? setPageNumberFQDN(1) : setPageNumberIP(1);
			} else {
				const sourceMessage =
					data?.msg || (typeof data === 'string' ? data : 'IOC delete request did not return success');
				const formattedError = buildIOCListsSupportError(
					FRONTEND_IOC_LISTS_ERROR_CODES.DELETE_ENTRY_FAILED,
					'IOC Lists Page / Delete Entry Failed',
					sourceMessage
				);
				toast.error(formattedError);
				setIOCListsSupportError(
					FRONTEND_IOC_LISTS_ERROR_CODES.DELETE_ENTRY_FAILED,
					'IOC Lists Page / Delete Entry Failed',
					sourceMessage
				);
			}
		} catch (error) {
			const sourceMessage = error?.message || String(error);
			const formattedError = buildIOCListsSupportError(
				FRONTEND_IOC_LISTS_ERROR_CODES.DELETE_ENTRY_FAILED,
				'IOC Lists Page / Delete Entry Request Error',
				sourceMessage
			);
			toast.error(formattedError);
			setIOCListsSupportError(
				FRONTEND_IOC_LISTS_ERROR_CODES.DELETE_ENTRY_FAILED,
				'IOC Lists Page / Delete Entry Request Error',
				sourceMessage
			);
		}
	};

	return (
		<Content currentMenu='configuration' headerTitle='IOC Lists'>
			{dataLoading ? (
				<Loader />
			) : iocData && Object.keys(iocData).length === 0 ? (
				<div>
					<NetworkError />
					{supportErrorMessage && (
						<div className={IOCListStyles.supportErrorCard}>
							<strong>Support Error:</strong>
							<code className={IOCListStyles.supportErrorCode}>{supportErrorMessage}</code>
							<button
								type='button'
								className={IOCListStyles.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
						</div>
					)}
				</div>
			) : (
				<div className={IOCListStyles.tables}>
					{supportErrorMessage && (
						<div className={IOCListStyles.supportErrorBanner}>
							<div className={IOCListStyles.supportErrorBannerText}>
								<strong>Support Error:</strong>
								<code className={IOCListStyles.supportErrorCode}>{supportErrorMessage}</code>
							</div>
							<button
								type='button'
								className={IOCListStyles.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
						</div>
					)}
					<div className={IOCListStyles.table}>
						<div className={IOCListStyles.table_heading}>
							<div className={IOCListStyles.table_header}>
								<h3 className={IOCListStyles.table_header_text}>IOC FQDN</h3>
								{Boolean(iocData?.isProvider) && (
									<button
										data-testid='add-fqdn'
										className={IOCListStyles.table_header_btn}
										onClick={() => setAddIOCModalStatus({ status: true, type: 'FQDN' })}>
										<svg className={IOCListStyles.table_header_btn_icon}>
											<use xlinkHref={`/assets/sprite.svg#icon-plus`} />
										</svg>
									</button>
								)}
							</div>
							<TextInput
								icon='search'
								placeholder='Search Here'
								value={searchFQDNKey}
								onChange={(e) => setSearchFQDNKey(e.target.value)}
								style={{ width: '15rem' }}
							/>
						</div>
						<Table
							containerStyle={{ height: '70vh' }}
							tableHead={
								<tr>
									{iocData.FQDNCols &&
										iocData.FQDNCols.length > 0 &&
										iocData.FQDNCols.map((col, index) => <th key={col + index}>{col}</th>)}
								</tr>
							}
							tableRow={
								FQDNTotalRows > 0 && FQDNRows.length > 0 ? (
									FQDNRows.map((rows, index) => (
										<tr key={index}>
											{rows &&
												rows.length > 0 &&
												rows.map((row) => (
													<Fragment key={row}>
														<td>{row}</td>
														{Boolean(iocData?.isProvider) && (
															<td>
																<TableButton
																	label='Delete'
																	icon='trash'
																	onClick={() => setDeleteIOCModalStatus({ status: true, type: 'FQDN', data: row })}
																/>
															</td>
														)}
													</Fragment>
												))}
										</tr>
									))
								) : (
									<tr>
										<td colSpan={iocData.FQDNCols && iocData.FQDNCols.length} style={{ textAlign: 'center' }}>
											No data found
										</td>
									</tr>
								)
							}
						/>
						<div className='paginationContainer'>
							<Pagination
								onPageChange={handleFQDNPageChange}
								rowsPerPage={pageLimit}
								totalPageSize={FQDNTotalRows}
								pageNumber={pageNumberFQDN}
							/>
						</div>
					</div>

					<div className={IOCListStyles.table}>
						<div className={IOCListStyles.table_heading}>
							<div className={IOCListStyles.table_header}>
								<h3 className={IOCListStyles.table_header_text}>IOC IPs</h3>
								{Boolean(iocData?.isProvider) && (
									<button
										data-testid='add-ip'
										className={IOCListStyles.table_header_btn}
										onClick={() => setAddIOCModalStatus({ status: true, type: 'IP' })}>
										<svg className={IOCListStyles.table_header_btn_icon}>
											<use xlinkHref={`/assets/sprite.svg#icon-plus`} />
										</svg>
									</button>
								)}
							</div>
							<TextInput
								icon='search'
								placeholder='Search Here'
								value={searchIPKey}
								onChange={(e) => setSearchIPKey(e.target.value)}
								style={{ width: '15rem' }}
							/>
						</div>
						<Table
							containerStyle={{ height: '70vh' }}
							tableHead={
								<tr>
									{iocData.IPSCols &&
										iocData.IPSCols.length > 0 &&
										iocData.IPSCols.map((col, index) => <th key={col + index}>{col}</th>)}
								</tr>
							}
							tableRow={
								IPTotalRows > 0 && IPRows.length > 0 ? (
									IPRows.map((rows, index) => (
										<tr key={index}>
											{rows &&
												rows.length > 0 &&
												rows.map((row) => (
													<Fragment key={row}>
														<td>{row}</td>
														{Boolean(iocData?.isProvider) && (
															<td>
																<TableButton
																	label='Delete'
																	icon='trash'
																	onClick={() => setDeleteIOCModalStatus({ status: true, type: 'IP', data: row })}
																/>
															</td>
														)}
													</Fragment>
												))}
										</tr>
									))
								) : (
									<tr>
										<td colSpan={iocData.IPSCols && iocData.IPSCols.length} style={{ textAlign: 'center' }}>
											No data found
										</td>
									</tr>
								)
							}
						/>

						<div className='paginationContainer'>
							<Pagination
								onPageChange={handleIPPageChange}
								rowsPerPage={pageLimit}
								totalPageSize={IPTotalRows}
								pageNumber={pageNumberIP}
							/>
						</div>
					</div>

					<div className={IOCListStyles.table}>
						<div className={IOCListStyles.table_heading}>
							<div className={IOCListStyles.table_header}>
								<h3 className={IOCListStyles.table_header_text}>IOC File Hashes</h3>
								{Boolean(iocData?.isProvider) && (
									<button
										data-testid='add-hash'
										className={IOCListStyles.table_header_btn}
										onClick={() => setAddIOCModalStatus({ status: true, type: 'Hash' })}>
										<svg className={IOCListStyles.table_header_btn_icon}>
											<use xlinkHref={`/assets/sprite.svg#icon-plus`} />
										</svg>
									</button>
								)}
							</div>
							<TextInput
								icon='search'
								placeholder='Search Here'
								value={searchHashKey}
								onChange={(e) => setSearchHashKey(e.target.value)}
								style={{ width: '15rem' }}
							/>
						</div>
						<Table
							containerStyle={{ height: '70vh' }}
							tableHead={
								<tr>
									{iocData.HashCols &&
										iocData.HashCols.length > 0 &&
										iocData.HashCols.map((col, index) => <th key={col + index}>{col}</th>)}
								</tr>
							}
							tableRow={
								HashTotalRows > 0 && HashRows.length > 0 ? (
									HashRows.map((rows, index) => (
										<tr key={index}>
											{rows &&
												rows.length > 0 &&
												rows.map((row) => (
													<Fragment key={row}>
														<td>{row}</td>
														{Boolean(iocData?.isProvider) && (
															<td>
																<TableButton
																	label='Delete'
																	icon='trash'
																	onClick={() => setDeleteIOCModalStatus({ status: true, type: 'Hash', data: row })}
																/>
															</td>
														)}
													</Fragment>
												))}
										</tr>
									))
								) : (
									<tr>
										<td colSpan={iocData.HashCols && iocData.HashCols.length} style={{ textAlign: 'center' }}>
											No data found
										</td>
									</tr>
								)
							}
						/>

						<div className='paginationContainer'>
							<Pagination
								onPageChange={handleHashPageChange}
								rowsPerPage={pageLimit}
								totalPageSize={HashTotalRows}
								pageNumber={pageNumberHash}
							/>
						</div>
					</div>
				</div>
			)}

			<AddIOCModal
				data={{ addIOCModalStatus, newIOCText, setNewIOCText, newIOCFile, setNewIOCFile, closeModal, handleAddIOC }}
			/>
			<DeleteIOCModal data={{ deleteIOCModalStatus, closeDeleteModal, handleDeleteIOC }} />
		</Content>
	);
};

export default IOCListsScreen;
